import { create } from 'zustand';
import type { User } from '@/types';
import { authService } from '@/services/AuthService';
import { socketService } from '@/services/SocketService';
import { socketHealthMonitor } from '@/services/SocketHealthMonitor';

/**
 * Auth status state machine:
 *   idle → checking → authenticated → (logout) → unauthenticated
 *                   → unauthenticated
 */
type AuthStatus = 'idle' | 'checking' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: User | null;
  status: AuthStatus;

  // Derived (for backward-compat with useAuth hook)
  isAuthenticated: boolean;
  isLoading: boolean;

  // Transitions
  setChecking: () => void;
  setAuthenticated: (user: User) => void;
  setUnauthenticated: () => void;

  // Actions
  login: () => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;

  // Legacy compat — kept temporarily, prefer setAuthenticated/setUnauthenticated
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'idle',
  isAuthenticated: false,
  isLoading: true,

  setChecking: () =>
    set({
      status: 'checking',
      isLoading: true,
    }),

  setAuthenticated: (user) =>
    set({
      user,
      status: 'authenticated',
      isAuthenticated: true,
      isLoading: false,
    }),

  setUnauthenticated: () =>
    set({
      user: null,
      status: 'unauthenticated',
      isAuthenticated: false,
      isLoading: false,
    }),

  // Legacy compat
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      status: user ? 'authenticated' : 'unauthenticated',
    }),

  setLoading: (isLoading) =>
    set({ isLoading }),

  login: () => {
    authService.login();
  },

  logout: async () => {
    // 1. Disconnect all sockets first (prevents stale events during cleanup)
    try {
      socketService.disconnectAll();
    } catch {
      // Best effort
    }

    // 2. Stop health monitoring
    try {
      socketHealthMonitor.stop();
    } catch {
      // Best effort
    }

    // 3. Reset all stores — import dynamically to avoid circular deps
    try {
      const { useFriendsStore } = await import('./friendsStore');
      useFriendsStore.getState().reset();
    } catch {
      // Store may not exist
    }

    try {
      const { useNotificationStore } = await import('./notificationStore');
      useNotificationStore.getState().reset();
    } catch {
      // Store may not exist
    }

    try {
      const { useChatStore } = await import('./chatStore');
      useChatStore.getState().reset();
    } catch {
      // Store may not exist
    }

    try {
      const { useQueueStore } = await import('./queueStore');
      useQueueStore.getState().reset();
    } catch {
      // Store may not exist
    }

    // 4. Clear persisted storage for current user
    clearAppStorage();

    // 5. Call server logout (clears cookies)
    try {
      await authService.logout();
    } catch {
      // Best effort — cookies may already be expired
    }

    // 6. Set auth state to unauthenticated
    set({
      user: null,
      status: 'unauthenticated',
      isAuthenticated: false,
      isLoading: false,
    });
  },

  checkAuth: async () => {
    set({ status: 'checking', isLoading: true });
    try {
      const user = await authService.getUser();
      if (user) {
        set({
          user,
          status: 'authenticated',
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          status: 'unauthenticated',
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch {
      set({
        user: null,
        status: 'unauthenticated',
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },
}));

/**
 * Clear all app-related localStorage and sessionStorage entries.
 * Called during logout to prevent cross-account data leaks.
 */
function clearAppStorage(): void {
  // Remove persisted store data
  const storageKeys = [
    'friends-storage',
    'notification-storage',
    'chat-storage',
    'queue-storage',
  ];

  for (const key of storageKeys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage may be unavailable
    }
  }

  // Remove legacy session artifacts
  const sessionKeys = [
    'auth_token',
    'auth_user',
    'user_data',
    'refresh_token',
    'session_id',
    'ws_ticket',
  ];

  for (const key of sessionKeys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // sessionStorage may be unavailable
    }
  }
}
